/**
 * VISA/CERTS: Certificate and qualification type definitions
 * Frontend mirror of backend/src/data/certificateTypes.js
 */

export const CERTIFICATE_TYPES = {
  // SAFETY - Mandatory
  stcw_basic: {
    code: 'stcw_basic',
    label: 'STCW Basic Safety Training',
    category: 'Safety',
    expires: true,
    expiryYears: 5,
    description: 'Required for ALL crew. Covers fire, survival, first aid, social responsibility.'
  },
  stcw_refresher: {
    code: 'stcw_refresher',
    label: 'STCW Refresher',
    category: 'Safety',
    expires: true,
    expiryYears: 5,
    description: 'Required to renew STCW after 5 years.'
  },
  adv_firefighting: {
    code: 'adv_firefighting',
    label: 'Advanced Firefighting',
    category: 'Safety',
    expires: true,
    expiryYears: 5,
    description: 'Beyond STCW basic - required for senior crew.'
  },
  crowd_crisis: {
    code: 'crowd_crisis',
    label: 'Crowd & Crisis Management',
    category: 'Safety',
    expires: false,
    description: 'Required on larger yachts (passenger vessel code).'
  },

  // MEDICAL
  eng1: {
    code: 'eng1',
    label: 'ENG1 Medical Certificate',
    category: 'Medical',
    expires: true,
    expiryYears: 2,
    description: 'MCA seafarer\'s medical fitness certificate.'
  },
  ml5: {
    code: 'ml5',
    label: 'ML5 Medical Certificate',
    category: 'Medical',
    expires: true,
    expiryYears: 2,
    description: 'Alternative to ENG1 for smaller vessels.'
  },
  dive_medical: {
    code: 'dive_medical',
    label: 'PADI/SSI Dive Medical',
    category: 'Medical',
    expires: true,
    expiryYears: 1,
    description: 'Required if crew does recreational or professional diving.'
  },
  mca_first_aid: {
    code: 'mca_first_aid',
    label: 'MCA Medical First Aid',
    category: 'Medical',
    expires: true,
    expiryYears: 5,
    description: 'Standard maritime first aid.'
  },
  mfaw: {
    code: 'mfaw',
    label: 'Medical First Aid (MFAW)',
    category: 'Medical',
    expires: true,
    expiryYears: 5,
    description: 'Advanced medical first aid - beyond STCW basic.'
  },
  pmc: {
    code: 'pmc',
    label: 'Proficiency in Medical Care (PMC)',
    category: 'Medical',
    expires: true,
    expiryYears: 5,
    description: 'Highest onboard medical qualification. Required for captains.'
  },
  efr: {
    code: 'efr',
    label: 'Emergency First Response (EFR)',
    category: 'Medical',
    expires: true,
    expiryYears: 2,
    description: 'CPR and first aid - often combined with dive training.'
  },
  yacht_medic: {
    code: 'yacht_medic',
    label: 'Yacht Medic Course',
    category: 'Medical',
    expires: false,
    description: 'Specialized yacht medical training.'
  },

  // DECK
  powerboat_l2: {
    code: 'powerboat_l2',
    label: 'RYA Powerboat Level 2',
    category: 'Deck',
    expires: false,
    description: 'Required for tender driving. Insurance requirement.'
  },
  yachtmaster_coastal: {
    code: 'yachtmaster_coastal',
    label: 'RYA/MCA Yachtmaster Coastal',
    category: 'Deck',
    expires: false,
    description: 'Skipper qualification for coastal waters.'
  },
  yachtmaster_offshore: {
    code: 'yachtmaster_offshore',
    label: 'RYA/MCA Yachtmaster Offshore',
    category: 'Deck',
    expires: false,
    description: 'Skipper qualification for offshore passages.'
  },
  yachtmaster_ocean: {
    code: 'yachtmaster_ocean',
    label: 'RYA/MCA Yachtmaster Ocean',
    category: 'Deck',
    expires: false,
    description: 'Highest Yachtmaster qualification - unlimited range.'
  },
  oow_3000: {
    code: 'oow_3000',
    label: 'Officer of the Watch (OOW) 3000GT',
    category: 'Deck',
    expires: true,
    expiryYears: 5,
    description: 'Required for officer positions on yachts under 3000GT.'
  },
  chief_mate_3000: {
    code: 'chief_mate_3000',
    label: 'Chief Mate 3000GT',
    category: 'Deck',
    expires: true,
    expiryYears: 5,
    description: 'Senior officer qualification.'
  },
  master_3000: {
    code: 'master_3000',
    label: 'Master 3000GT',
    category: 'Deck',
    expires: true,
    expiryYears: 5,
    description: 'Captain qualification for yachts under 3000GT.'
  },
  master_500: {
    code: 'master_500',
    label: 'Master 500GT',
    category: 'Deck',
    expires: true,
    expiryYears: 5,
    description: 'Captain qualification for smaller yachts.'
  },
  aec: {
    code: 'aec',
    label: 'Yacht Rating (AEC)',
    category: 'Deck',
    expires: false,
    description: 'Approved Engine Course - basic engineering for deckhands.'
  },
  pwc: {
    code: 'pwc',
    label: 'RYA Personal Watercraft (Jet Ski)',
    category: 'Deck',
    expires: false,
    description: 'License to operate jet skis / water toys.'
  },

  // COMMUNICATIONS
  vhf_src: {
    code: 'vhf_src',
    label: 'VHF/SRC Radio Certificate',
    category: 'Communications',
    expires: false,
    description: 'Short Range Certificate - required for bridge watchkeeping and tender ops.'
  },
  gmdss_goc: {
    code: 'gmdss_goc',
    label: 'GMDSS General Operator Certificate (GOC)',
    category: 'Communications',
    expires: false,
    description: 'Required for vessels with GMDSS equipment.'
  },
  gmdss_roc: {
    code: 'gmdss_roc',
    label: 'GMDSS Restricted Operator Certificate (ROC)',
    category: 'Communications',
    expires: false,
    description: 'Restricted GMDSS for smaller vessels.'
  },

  // SECURITY
  pdsd: {
    code: 'pdsd',
    label: 'PDSD (Proficiency in Designated Security Duties)',
    category: 'Security',
    expires: false,
    description: 'Required security training for all crew on ISPS-compliant vessels.'
  },
  sso: {
    code: 'sso',
    label: 'SSO (Ship Security Officer)',
    category: 'Security',
    expires: false,
    description: 'For the designated security officer on board.'
  },

  // WATER SPORTS
  padi_ow: {
    code: 'padi_ow',
    label: 'PADI Open Water / Advanced',
    category: 'Water Sports',
    expires: false,
    description: 'Scuba diving certification.'
  },
  padi_dm: {
    code: 'padi_dm',
    label: 'PADI Divemaster / Instructor',
    category: 'Water Sports',
    expires: false,
    description: 'Professional diving qualification.'
  },
  rescue_diver: {
    code: 'rescue_diver',
    label: 'Rescue Diver',
    category: 'Water Sports',
    expires: false,
    description: 'Advanced rescue diving certification.'
  },

  // INTERIOR
  food_hygiene_l2: {
    code: 'food_hygiene_l2',
    label: 'Food Hygiene Level 2',
    category: 'Interior',
    expires: false,
    description: 'Required for anyone handling food. Basic legal requirement.'
  },
  food_hygiene_l3: {
    code: 'food_hygiene_l3',
    label: 'Food Hygiene Level 3',
    category: 'Interior',
    expires: false,
    description: 'Advanced food safety - for chief stews and crew managing galley.'
  },
  wset_l1: {
    code: 'wset_l1',
    label: 'WSET Level 1 Wine',
    category: 'Interior',
    expires: false,
    description: 'Wine and Spirit Education Trust - basic wine service.'
  },
  wset_l2: {
    code: 'wset_l2',
    label: 'WSET Level 2 Wine',
    category: 'Interior',
    expires: false,
    description: 'Intermediate wine knowledge - expected on premium yachts.'
  },
  wset_l3: {
    code: 'wset_l3',
    label: 'WSET Level 3 Wine',
    category: 'Interior',
    expires: false,
    description: 'Advanced wine qualification.'
  },
  silver_service: {
    code: 'silver_service',
    label: 'Silver Service Training',
    category: 'Interior',
    expires: false,
    description: 'Formal table service training.'
  },
  barista_mixology: {
    code: 'barista_mixology',
    label: 'Barista / Mixology',
    category: 'Interior',
    expires: false,
    description: 'Coffee and cocktail preparation.'
  },
  floristry: {
    code: 'floristry',
    label: 'Floristry',
    category: 'Interior',
    expires: false,
    description: 'Flower arrangement for yacht interiors.'
  },
  laundry_housekeeping: {
    code: 'laundry_housekeeping',
    label: 'Laundry & Housekeeping',
    category: 'Interior',
    expires: false,
    description: 'Professional textile care and housekeeping certification.'
  },

  // ENGINEERING
  aec_eng: {
    code: 'aec_eng',
    label: 'AEC (Approved Engine Course)',
    category: 'Engineering',
    expires: false,
    description: 'Basic engineering - often done by deckhands wanting to upskill.'
  },
  y4: {
    code: 'y4',
    label: 'Y4 Engineer',
    category: 'Engineering',
    expires: true,
    expiryYears: 5,
    description: 'Entry-level yacht engineering certificate.'
  },
  y3: {
    code: 'y3',
    label: 'Y3 Engineer',
    category: 'Engineering',
    expires: true,
    expiryYears: 5,
    description: 'Junior engineer qualification.'
  },
  y2: {
    code: 'y2',
    label: 'Y2 Engineer',
    category: 'Engineering',
    expires: true,
    expiryYears: 5,
    description: 'Second engineer qualification.'
  },
  y1: {
    code: 'y1',
    label: 'Y1 Engineer',
    category: 'Engineering',
    expires: true,
    expiryYears: 5,
    description: 'Chief engineer qualification.'
  },
  eto: {
    code: 'eto',
    label: 'ETO (Electro-Technical Officer)',
    category: 'Engineering',
    expires: true,
    expiryYears: 5,
    description: 'Specialist electronics/AV/IT qualification. Highly in demand.'
  },
  rac: {
    code: 'rac',
    label: 'Refrigeration & Air Conditioning',
    category: 'Engineering',
    expires: false,
    description: 'Specialist certification for yacht HVAC systems.'
  },

  // GALLEY
  ships_cook: {
    code: 'ships_cook',
    label: "Ship's Cook Certificate",
    category: 'Galley',
    expires: false,
    description: 'MCA requirement for vessels with 10+ crew.'
  },
  culinary_diploma: {
    code: 'culinary_diploma',
    label: 'Culinary Diploma / Degree',
    category: 'Galley',
    expires: false,
    description: 'Formal culinary qualification (City & Guilds, Le Cordon Bleu, etc.)'
  },
  allergen: {
    code: 'allergen',
    label: 'Allergen Awareness',
    category: 'Galley',
    expires: false,
    description: 'Allergen management certification.'
  },

  // LEADERSHIP
  helm_op: {
    code: 'helm_op',
    label: 'HELM Operational',
    category: 'Leadership',
    expires: false,
    description: 'Human Element Leadership & Management - operational level.'
  },
  helm_mgmt: {
    code: 'helm_mgmt',
    label: 'HELM Management',
    category: 'Leadership',
    expires: false,
    description: 'HELM at management level - required for senior officers.'
  }
};

// Categories for grouping
export const CERTIFICATE_CATEGORIES = [
  'Safety',
  'Medical',
  'Deck',
  'Communications',
  'Security',
  'Water Sports',
  'Interior',
  'Engineering',
  'Galley',
  'Leadership'
];

// Grouped by category for UI
export const CERTIFICATE_GROUPS = CERTIFICATE_CATEGORIES.map(category => ({
  label: category,
  certificates: Object.keys(CERTIFICATE_TYPES).filter(
    code => CERTIFICATE_TYPES[code].category === category
  )
}));

// Common certificates for quick-add buttons
export const COMMON_CERTIFICATES = ['stcw_basic', 'eng1', 'powerboat_l2', 'food_hygiene_l2', 'vhf_src'];

// Mandatory certificates (most positions require these)
export const MANDATORY_CERTIFICATES = ['stcw_basic', 'eng1'];

/**
 * Get certificate label by code
 */
export function getCertificateLabel(code) {
  return CERTIFICATE_TYPES[code]?.label || code;
}

/**
 * Calculate certificate status based on expiry date
 */
export function calculateCertificateStatus(expiryDate, expires) {
  if (!expires) return 'valid';
  if (!expiryDate) return 'valid';

  const expiry = new Date(expiryDate);
  const now = new Date();
  const sixtyDaysFromNow = new Date();
  sixtyDaysFromNow.setDate(sixtyDaysFromNow.getDate() + 60);

  if (expiry < now) return 'expired';
  if (expiry < sixtyDaysFromNow) return 'renewal_needed';
  return 'valid';
}

/**
 * Get status badge styling
 */
export function getStatusBadgeClass(status) {
  switch (status) {
    case 'expired':
      return 'bg-red-100 text-red-800';
    case 'renewal_needed':
      return 'bg-yellow-100 text-yellow-800';
    case 'valid':
    default:
      return 'bg-green-100 text-green-800';
  }
}

/**
 * Get status label
 */
export function getStatusLabel(status) {
  switch (status) {
    case 'expired':
      return 'Expired';
    case 'renewal_needed':
      return 'Expiring Soon';
    case 'pending':
      return 'Pending';
    case 'valid':
    default:
      return 'Valid';
  }
}

/**
 * Get certificates by category
 */
export function getCertificatesByCategory(category) {
  return Object.entries(CERTIFICATE_TYPES)
    .filter(([_, cert]) => cert.category === category)
    .map(([code, cert]) => ({ code, ...cert }));
}
